/**
 * Platform shim layer — browser implementation.
 *
 * Mirrors the public surface of BigxBookDesktop/src/platform/index.ts so that
 * every module ported from Desktop compiles unchanged, with only its import
 * line pointing here. This is the ONLY layer in `src/` that knows it is
 * running in a browser rather than Electron.
 *
 * Desktop parity notes:
 *   • `secureStore`  — no browser equivalent of DPAPI. See BIGXBOOK_WEB_PLAN §7.2.
 *   • `printer.tcpPrint`   — impossible in a browser; throws.
 *   • `printer.listSystem` — the print dialog owns printer choice; returns [].
 */

// ─── kvStore ──────────────────────────────────────────────────────────────────
// Desktop: electron-store over IPC. Web: localStorage.
// Async signatures are preserved even though localStorage is synchronous, so
// ported callers keep their `await`s.

const KV_PREFIX = 'bigxbook:';

export const kvStore = {
  async getString(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(KV_PREFIX + key);
    } catch {
      // Private mode / blocked site data.
      return null;
    }
  },
  async set(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(KV_PREFIX + key, value);
    } catch {
      /* quota or blocked storage — non-fatal */
    }
  },
  async delete(key: string): Promise<void> {
    try {
      localStorage.removeItem(KV_PREFIX + key);
    } catch {
      /* ignore */
    }
  },
};

// ─── secureStore ──────────────────────────────────────────────────────────────
/**
 * SECURITY: Desktop backs this with Electron `safeStorage` (Windows DPAPI).
 * A browser has no equivalent — anything reachable from JS is reachable by XSS.
 *
 * This is a deliberately thin placeholder so Phase 0 can boot. Before Phase 1
 * ships, the refresh token must move to an httpOnly cookie and the access token
 * to memory only (BIGXBOOK_WEB_PLAN §7.2, open item 1). Until then it is
 * session-scoped rather than persistent, so a closed tab does not leave a
 * long-lived credential on a shared machine.
 */
const SECURE_PREFIX = 'bigxbook-secure:';

export const secureStore = {
  async get(key: string): Promise<string | null> {
    try {
      return sessionStorage.getItem(SECURE_PREFIX + key);
    } catch {
      return null;
    }
  },
  async set(key: string, value: string): Promise<void> {
    try {
      sessionStorage.setItem(SECURE_PREFIX + key, value);
    } catch {
      /* ignore */
    }
  },
  async delete(key: string): Promise<void> {
    try {
      sessionStorage.removeItem(SECURE_PREFIX + key);
    } catch {
      /* ignore */
    }
  },
};

// ─── netInfo ──────────────────────────────────────────────────────────────────
// Desktop refines navigator.onLine with a main-process reachability check.
// `navigator.onLine` only reports whether a link exists, not whether the API is
// reachable (captive portals, LAN-only, backend down), so we confirm with a ping.

import { notifyError, notifySuccess } from '@/store/useToastStore';
import { API_BASE_URL } from '../config';

const PING_TIMEOUT_MS = 3000;

export const netInfo = {
  async isOnline(): Promise<boolean> {
    if (!navigator.onLine) return false;
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
      // GET rather than HEAD. Nest maps HEAD to the GET handler so both answer
      // 200, but GET is explicit and avoids relying on that mapping.
      // `no-store` so a cached 200 never masks an unreachable backend.
      const res = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        cache: 'no-store',
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      // Any HTTP answer proves reachability, including a degraded health
      // status — this asks "can I reach the API", not "is the API happy".
      return res.status > 0;
    } catch {
      return false;
    }
  },
  subscribe(listener: (online: boolean) => void): () => void {
    const on = () => listener(true);
    const off = () => listener(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  },
};

// ─── appState ─────────────────────────────────────────────────────────────────
// Identical to Desktop, plus visibilitychange — a backgrounded browser tab is
// not the same as an unfocused window, and only visibility fires on mobile.

export const appState = {
  current(): 'active' | 'background' {
    return document.visibilityState === 'visible' && document.hasFocus()
      ? 'active'
      : 'background';
  },
  subscribe(listener: (state: 'active' | 'background') => void): () => void {
    const emit = () => listener(appState.current());
    window.addEventListener('focus', emit);
    window.addEventListener('blur', emit);
    document.addEventListener('visibilitychange', emit);
    return () => {
      window.removeEventListener('focus', emit);
      window.removeEventListener('blur', emit);
      document.removeEventListener('visibilitychange', emit);
    };
  },
};

// ─── appInfo ──────────────────────────────────────────────────────────────────
// Desktop reads the Electron app version and userData path. In a browser the
// version is a build-time constant and there is no user-data path.

export const appInfo = {
  getVersion: async (): Promise<string> =>
    (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.0.0-dev',

  /**
   * No filesystem path exists in a browser. Returns a stable synthetic value so
   * ported callers that only use this for display or as a name seed still work.
   * `deviceIdentity.getDeviceName()` is rewritten for web and does not call this.
   */
  getUserDataPath: async (): Promise<string> => 'browser://indexeddb',
};

// ─── printer ──────────────────────────────────────────────────────────────────

/** Thrown when a ported module reaches a transport the browser cannot provide. */
export class UnsupportedOnWebError extends Error {
  constructor(what: string) {
    super(`${what} is not available in a browser`);
    this.name = 'UnsupportedOnWebError';
  }
}

/**
 * Injects HTML into a hidden same-origin iframe and invokes the print dialog.
 * Replaces Desktop's `printer.printHtml`, which prints silently to a named OS
 * printer via the main process. In a browser the viewer chooses the printer,
 * unless Chrome is launched with `--kiosk-printing`.
 */
function printHtmlViaIframe(html: string): Promise<{ ok: boolean; message?: string }> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    // Off-screen rather than display:none — a hidden iframe does not lay out,
    // and some engines then print a blank page.
    iframe.setAttribute('aria-hidden', 'true');
    // Sandbox the print frame.
    //
    // `srcdoc` is assembled by string-interpolating merchant-entered catalog and
    // customer text (product names, SKUs, categories, notes), and almost none of
    // the report screens escape it. Without a sandbox that HTML runs in the app's
    // OWN origin, so a product name containing a <script> tag — which can arrive
    // by CSV import or by sync from another device — would execute with access to
    // localStorage, where the auth and refresh tokens live.
    //
    // Desktop is not exposed this way: it renders reports in an Electron window
    // created with `javascript: false`.
    //
    // `allow-same-origin` is required because the parent reaches into
    // contentWindow to call print(); `allow-modals` is required for the print
    // dialog itself. `allow-scripts` is deliberately NOT granted — omitting it is
    // what stops injected script from running, while static report markup, CSS
    // and images continue to render and print normally.
    iframe.setAttribute('sandbox', 'allow-same-origin allow-modals');
    // Real A4 dimensions, moved OFF-SCREEN — not collapsed and not hidden.
    //
    // This used to be width:0;height:0;visibility:hidden, which is the very
    // thing the note above warns against: a frame with no box never lays its
    // content out, so print() had nothing to rasterise and every saved PDF came
    // out blank. Sizing it to a real A4 page also means the print engine sees
    // the layout the template was designed against, rather than reflowing it
    // at some arbitrary width.
    iframe.style.cssText =
      'position:fixed;left:-10000px;top:0;width:210mm;height:297mm;border:0;';

    let settled = false;
    const cleanup = (result: { ok: boolean; message?: string }) => {
      if (settled) return;
      settled = true;
      // Defer removal: Safari aborts the job if the frame is torn down too soon.
      setTimeout(() => iframe.remove(), 1000);
      resolve(result);
    };

    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (!win) return cleanup({ ok: false, message: 'Print frame unavailable' });

      // `onload` fires when the document is parsed, which is earlier than the
      // first layout and paint. Printing on that tick can still capture an
      // unlaid-out page. Two animation frames put us after a committed layout,
      // and the short timeout gives webfonts and any data: images a chance to
      // settle before the snapshot.
      const printWhenReady = () => {
        try {
          win.focus();
          win.print();
          cleanup({ ok: true });
        } catch (err) {
          cleanup({ ok: false, message: (err as Error).message });
        }
      };

      requestAnimationFrame(() =>
        requestAnimationFrame(() => setTimeout(printWhenReady, 150)),
      );
    };
    iframe.onerror = () => cleanup({ ok: false, message: 'Print frame failed to load' });

    document.body.appendChild(iframe);
    iframe.srcdoc = html;
  });
}

export const printer = {
  /**
   * Raw TCP ESC/POS. No browser API can open a TCP socket, so this always
   * fails. Web billing uses `printHtml` instead — see BIGXBOOK_WEB_PLAN §5.3.
   */
  async tcpPrint(_args: {
    host: string;
    port: number;
    payload: Uint8Array;
    connectTimeoutMs?: number;
    writeTimeoutMs?: number;
  }): Promise<{ ok: boolean; errorCode?: string; message?: string }> {
    return {
      ok: false,
      errorCode: 'transport_unavailable',
      message: 'Network thermal printing is not available in the browser. Use system print.',
    };
  },

  /**
   * Desktop enumerates Windows printers so Settings can offer a picker. The
   * browser print dialog owns that choice, so this returns an empty list and
   * the picker UI stays hidden on web.
   */
  async listSystem(): Promise<
    Array<{ name: string; displayName: string; isDefault: boolean; status: number }>
  > {
    return [];
  },

  /** `deviceName` and `copies` are ignored — the print dialog controls both. */
  async printHtml(args: {
    html: string;
    deviceName?: string;
    copies?: number;
  }): Promise<{ ok: boolean; message?: string }> {
    return printHtmlViaIframe(args.html);
  },
};

// ─── pdf ──────────────────────────────────────────────────────────────────────
// Desktop renders via Chromium printToPDF in the main process. In a browser the
// same print dialog offers "Save as PDF", so this routes through printHtml.
// The filename comes from the document title, which is the best a browser allows.

export const pdf = {
  async saveFromHtml(args: {
    html: string;
    defaultFileName: string;
  }): Promise<{ ok: boolean; message?: string }> {
    return printHtmlViaIframe(args.html);
  },
};

// ─── file ─────────────────────────────────────────────────────────────────────
// Desktop uses native Save-As / Open dialogs. Web uses a download link and a
// file input. Both resolve to the same shapes the ported exporters expect.

export const file = {
  async saveText(args: {
    content: string;
    defaultFileName: string;
    filters?: Array<{ name: string; extensions: string[] }>;
  }): Promise<{ ok: boolean; canceled?: boolean; filePath?: string; message?: string }> {
    try {
      const blob = new Blob([args.content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = args.defaultFileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after the download has been handed to the browser.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      // Every export button in Reports lands here and none of them said
      // anything — a download that failed looked the same as one that worked.
      notifySuccess(`Downloaded ${args.defaultFileName}`);
      return { ok: true, filePath: args.defaultFileName };
    } catch (err) {
      notifyError(`Could not download ${args.defaultFileName}: ${(err as Error).message}`);
      return { ok: false, message: (err as Error).message };
    }
  },

  async openText(args?: {
    title?: string;
    filters?: Array<{ name: string; extensions: string[] }>;
  }): Promise<{ ok: boolean; canceled?: boolean; content?: string; fileName?: string; message?: string }> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      const exts = args?.filters?.flatMap((f) => f.extensions.map((e) => `.${e}`)) ?? [];
      if (exts.length) input.accept = exts.join(',');

      input.onchange = async () => {
        const f = input.files?.[0];
        if (!f) return resolve({ ok: false, canceled: true });
        try {
          resolve({ ok: true, content: await f.text(), fileName: f.name });
        } catch (err) {
          resolve({ ok: false, message: (err as Error).message });
        }
      };
      // A cancelled picker fires no event in most browsers; `cancel` covers
      // those that support it. Callers must tolerate a promise that never
      // settles on older engines, same as Desktop tolerates a closed dialog.
      input.oncancel = () => resolve({ ok: false, canceled: true });
      input.click();
    });
  },
};
